import type { ExpertObject } from 'commons/types';
import { underdashToCamelCase } from 'utils/string';

const sortExperts = (a: ExpertObject, b: ExpertObject) => {
  const { profile: profileA } = a;
  const { profile: profileB } = b;
  let result = 0;

  if (profileA && profileB) {
    const { firstName: firstNameA = '', lastName: lastNameA = '' } = profileA;
    const { firstName: firstNameB = '', lastName: lastNameB = '' } = profileB;

    if (firstNameA !== firstNameB) {
      result = firstNameA.localeCompare(firstNameB);
    } else {
      result = lastNameA.localeCompare(lastNameB);
    }
  } else if (profileA) {
    return -1;
  } else if (profileB) {
    return 1;
  }

  return result;
};

const numericSort = (
  sortingField: string,
  a: Record<string, any>,
  b: Record<string, any>,
): number => a[sortingField] - b[sortingField];

const fieldsHaveValue = (
  sortingField: string,
  obj1: Record<string, any>,
  obj2: Record<string, any>,
): boolean =>
  (obj1[sortingField] || obj1[sortingField] === 0) &&
  (obj2[sortingField] || obj2[sortingField] === 0);

const dynamicSort =
  (property: string): ((...args: Array<any>) => any) =>
  (obj1: Record<string, any>, obj2: Record<string, any>): number => {
    // First make property camelCase and check if there's a - at the beginning
    let sortingField = underdashToCamelCase(property);
    let orderField = 1;

    if (sortingField && sortingField.length && sortingField[0] === '-') {
      orderField = -1;
      sortingField = sortingField.substring(1);
    }

    // Now we perform the sorting
    if (fieldsHaveValue(sortingField, obj1, obj2)) {
      if (typeof obj1[sortingField] !== 'number') {
        return String(obj1[sortingField]).localeCompare(String(obj2[sortingField])) * orderField;
      }

      // If the sorting fields are numeric, we do a Numeric order
      return numericSort(sortingField, obj1, obj2) * orderField;
    }

    return 0;
  };

/**
 * @description
 * This is a sorting function for any array of Objects
 * It expects many fields as different arguments
 * Fields can be used Ascendent or Descendent
 * @param {string[]} args sorting fields to order by.
 * The sorting fields can be underdashed like 'rank_score'
 * The sorting fields can have a '-' sign as the first character
 * to indicate descendance order
 */
const dynamicSortMultipleFields = (...args: string[]) => {
  /*
   * save the arguments object as it will be overwritten
   * note that arguments object is an array-like object
   * consisting of the names of the properties to sort by
   */
  const props = args;
  return (obj1: Record<string, any>, obj2: Record<string, any>) => {
    let i = 0;
    let result = 0;
    const numberOfProperties = props.length;

    /*
     * try getting a different result from 0 (equal)
     * as long as we have extra properties to compare
     */
    while (result === 0 && i < numberOfProperties) {
      result = dynamicSort(props[i])(obj1, obj2);
      i += 1;
    }

    return result;
  };
};

export { sortExperts, dynamicSortMultipleFields };
